import { isEmpty } from "lodash-es";
import React, {
    Context,
    PropsWithChildren,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";

import { isDefined } from "@utils/helpers";

import BreakpointsService, {
    BreakpointMinSizes,
} from "@services/breakpoint.service";

export type BreakpointMatches = {
    [key in BreakpointMinSizes]?: boolean;
};

type mediaQueryList = {
    [key in BreakpointMinSizes]?: any;
};

export type BreakpointContextValues = {
    breakpoints: BreakpointMatches;
};

const defaultContext: BreakpointContextValues = {
    breakpoints: {},
};

const BreakpointContext: Context<BreakpointContextValues> =
    React.createContext<BreakpointContextValues>(defaultContext);

type Props = {
    children?: ReactNode;
};

const BreakpointProvider = ({ children }: Props) => {
    const breakpointsObject = BreakpointsService.getBreakpointsObject()?.min;
    const [queryMatch, setQueryMatch] = useState<BreakpointMatches>({});
    const [isAttached, setIsAttached] = useState(false);

    useEffect(() => {
        const mediaQueryLists: mediaQueryList = {};
        const keys: BreakpointMinSizes[] = Object.keys(
            breakpointsObject,
        ) as BreakpointMinSizes[];

        const handleQueryListener = () => {
            const updatedMatches: BreakpointMatches = keys.reduce(
                (acc: BreakpointMatches, media) => {
                    acc[media as BreakpointMinSizes] = !!(
                        mediaQueryLists[media] && mediaQueryLists[media].matches
                    );
                    return acc;
                },
                {},
            );

            setQueryMatch(updatedMatches);
        };

        if (window && window.matchMedia && !isAttached) {
            const matches: mediaQueryList = {};
            keys.forEach(media => {
                mediaQueryLists[media] = window.matchMedia(
                    breakpointsObject[media],
                );
                matches[media] = mediaQueryLists[media].matches;
            });
            setQueryMatch(matches);
            setIsAttached(true);
            keys.forEach(media => {
                mediaQueryLists[media].addEventListener(
                    "change",
                    handleQueryListener,
                );
            });
        }

        return () => {
            if (isAttached) {
                keys.forEach(media => {
                    mediaQueryLists[media].removeEventListener(
                        "change",
                        handleQueryListener,
                    );
                });
            }
        };
    }, []);

    return (
        <BreakpointContext.Provider value={{ breakpoints: queryMatch }}>
            {children}
        </BreakpointContext.Provider>
    );
};

const withBreakpoints = <T extends {}>(
    WrappedComponent: React.ComponentType<T>,
    FallbackComponent?: React.FC,
) => {
    const BreakpointsInitialzedBoundary = ({
        children,
    }: PropsWithChildren<{}>) => {
        const { breakpoints } = useBreakpoint();

        if (isEmpty(breakpoints) && isDefined(FallbackComponent)) {
            return <FallbackComponent />;
        }

        return children;
    };

    const ComponentWithBreakpoints = (props: T) => {
        return (
            <BreakpointProvider>
                <BreakpointsInitialzedBoundary>
                    <WrappedComponent {...props} />
                </BreakpointsInitialzedBoundary>
            </BreakpointProvider>
        );
    };

    return ComponentWithBreakpoints;
};

const useBreakpoint = (): BreakpointContextValues => {
    const context = useContext<BreakpointContextValues>(BreakpointContext);
    if (context === undefined) {
        throw new Error(
            "useBreakpointContext can only be used inside BreakpointProvider",
        );
    }
    return context;
};

export {
    useBreakpoint,
    BreakpointProvider,
    BreakpointContext,
    withBreakpoints,
};
